import { Button, Flex, Group, Modal, PasswordInput, Text } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { memo, useEffect } from "react";
import {
  errorShowNotification,
  successShowNotification,
} from "src/lib/mantine";
import { useResetUserPasswordMutation } from "src/service/AdminApi/AdminUsers";
import { z } from "zod";

const resetPasswordSchema = z
  .object({
    newPassword: z.string().min(6, "パスワードは6文字以上で入力してください"),
    confirmPassword: z
      .string()
      .min(6, "パスワードは6文字以上で入力してください"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "パスワードが一致しません",
    path: ["confirmPassword"],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

interface ResetPasswordModalProps {
  opened: boolean;
  onClose: () => void;
  userId: string;
  userName?: string;
  userEmail?: string;
  userRole?: string;
  userNickname?: string;
  onSuccess?: () => void;
}

const ResetPasswordModal = ({
  opened,
  onClose,
  userId,
  userName,
  userEmail,
  userRole,
  userNickname,
  onSuccess,
}: ResetPasswordModalProps) => {
  const [resetUserPassword, { isLoading }] = useResetUserPasswordMutation();

  const form = useForm<ResetPasswordFormValues>({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validate: zodResolver(resetPasswordSchema),
  });

  useEffect(() => {
    if (opened) {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const handleSubmit = async (values: ResetPasswordFormValues) => {
    try {
      await resetUserPassword({
        userId,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      }).unwrap();

      successShowNotification("パスワードをリセットしました");
      onSuccess?.();
      onClose();
    } catch (error) {
      errorShowNotification("パスワードのリセットに失敗しました");
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="パスワードリセット"
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Flex direction="column" gap="md">
          {(userName || userEmail || userNickname) && (
            <Flex
              direction="column"
              gap="xs"
              p="sm"
              style={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}
            >
              {userName && (
                <Text size="sm" color="dark">
                  <Text component="span" weight={500}>
                    氏名:
                  </Text>{" "}
                  {userName}
                </Text>
              )}
              {userNickname && (
                <Text size="sm" color="dark">
                  <Text component="span" weight={500}>
                    ニックネーム:
                  </Text>{" "}
                  {userNickname}
                </Text>
              )}
              {userEmail && (
                <Text size="sm" color="dark">
                  <Text component="span" weight={500}>
                    メール:
                  </Text>{" "}
                  {userEmail}
                </Text>
              )}
              {userRole && (
                <Text size="sm" color="dark">
                  <Text component="span" weight={500}>
                    役割:
                  </Text>{" "}
                  {userRole}
                </Text>
              )}
              <Text size="xs" color="dark">
                <Text component="span" weight={500}>
                  ID:
                </Text>{" "}
                {userId}
              </Text>
            </Flex>
          )}

          <PasswordInput
            label="新しいパスワード"
            placeholder="6文字以上で入力してください"
            required
            {...form.getInputProps("newPassword")}
          />

          <PasswordInput
            label="パスワード確認"
            placeholder="新しいパスワードを再入力してください"
            required
            {...form.getInputProps("confirmPassword")}
          />

          <Group position="right" mt="md">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              キャンセル
            </Button>
            <Button type="submit" color="red" loading={isLoading}>
              パスワードリセット
            </Button>
          </Group>
        </Flex>
      </form>
    </Modal>
  );
};

export const ResetPasswordModalMemo = memo(ResetPasswordModal);
